// src/features/supplierPedidos/hooks.ts
import { useQuery } from "@tanstack/react-query";
import { periodToSaoPauloRange } from "@/lib/dates";
import { getSupplierPedidos } from "./api";
import type {
  SupplierPedidosSummary,
  SupplierProductRow,
  SupplierStoreRow,
} from "./types";

// ---------------------------------------------------------------------------
// Query hook
// ---------------------------------------------------------------------------

/**
 * Fetches the supplier pedidos summary for the given TimeFilterBar period.
 * Automatically computes from/to in São Paulo local time.
 * Skips the query when the period cannot produce a valid date range.
 */
export function useSupplierPedidos(
  period: string,
  customRange?: { from: string; to: string },
): {
  data: SupplierPedidosSummary | undefined;
  isLoading: boolean;
  isError: boolean;
} {
  const range = periodToSaoPauloRange(
    period,
    customRange?.from,
    customRange?.to,
  );

  return useQuery({
    queryKey: ["supplierPedidos", range?.from, range?.to],
    queryFn: () => getSupplierPedidos(range!.from, range!.to),
    enabled: !!range,
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Sort helpers (exported for tests and page use)
// ---------------------------------------------------------------------------

export type ProductSortKey = "value" | "alpha";
export type StoreSortKey = "name" | "value";

/**
 * Returns a new sorted array of products.
 * value: descending by total_brl | alpha: ascending by product_name
 */
export function sortByProduct(
  products: SupplierProductRow[],
  key: ProductSortKey,
): SupplierProductRow[] {
  return [...products].sort((a, b) =>
    key === "value"
      ? b.total_brl - a.total_brl
      : a.product_name.localeCompare(b.product_name, "pt-BR"),
  );
}

/**
 * Returns a new sorted array of stores.
 * name: ascending by store_name | value: descending by total_brl
 */
export function sortByStore(
  stores: SupplierStoreRow[],
  key: StoreSortKey,
): SupplierStoreRow[] {
  return [...stores].sort((a, b) =>
    key === "name"
      ? a.store_name.localeCompare(b.store_name, "pt-BR")
      : b.total_brl - a.total_brl,
  );
}

// ---------------------------------------------------------------------------
// Filter helpers (exported for tests and page use)
// ---------------------------------------------------------------------------

/**
 * Filters products by product_name (case-insensitive). Returns all when query is empty.
 */
export function filterProducts(
  products: SupplierProductRow[],
  query: string,
): SupplierProductRow[] {
  const q = query.trim().toLowerCase();
  if (!q) return products;
  return products.filter((p) => p.product_name.toLowerCase().includes(q));
}

/**
 * Filters stores by store_name or client_name (case-insensitive). Returns all when query is empty.
 */
export function filterStores(
  stores: SupplierStoreRow[],
  query: string,
): SupplierStoreRow[] {
  const q = query.trim().toLowerCase();
  if (!q) return stores;
  return stores.filter(
    (s) =>
      s.store_name.toLowerCase().includes(q) ||
      s.client_name.toLowerCase().includes(q),
  );
}

/**
 * Filters products to a single product_id. Pass "" to return all.
 */
export function applyProductFilter(
  list: SupplierProductRow[],
  productId: string,
): SupplierProductRow[] {
  if (!productId) return list;
  return list.filter((p) => p.product_id === productId);
}

/**
 * Filters stores by store_id and/or client_name (AND logic). Pass "" for either to skip that filter.
 */
export function applyStoreFilter(
  list: SupplierStoreRow[],
  storeId: string,
  clientName: string,
): SupplierStoreRow[] {
  if (!storeId && !clientName) return list;
  return list.filter(
    (s) =>
      (!storeId || s.store_id === storeId) &&
      (!clientName || s.client_name === clientName),
  );
}
